import React, { useState, useEffect, useRef } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import ChatInput from './ChatInput';
import ContactMobil from './ContactMobil';
import ContactDesktop from './ContactDesktop';
import ChatHeader from './ChatHeader';
import LoadingProcess from './LoadingProcess';
import MessagesArea from './MessagesArea/MessagesArea';
import AuthService from '../services/authService';
import ApiClient from '../services/apiClient';
import TokenService from '../services/tokenService';
import { iniciarConexionAbly } from '../contexts/AblyMinimal';

const ChatWindow = () => {
    // Estado principal
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const [userSystem, setUserSystem] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [openConfi, setOpenConfig] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [contactActive, setContactActive] = useState(null);
    const [messagesUser, setMessagesUser] = useState([]);
    const [isLoadingMessage, setIsLoadingMessage] = useState(false);
    const [isLoadingCurrentMode, setIsLoadingMode] = useState(false);
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);
    const [isMessagesReady, setIsMessagesReady] = useState(false);
    const [typeMessagePermitted, setTypeMessagePermitted] = useState(['text']);
    // Referencias para tracking
    const isMobile = useMediaQuery('(max-width:920px)');
    const [conexionAbly, setConexionAbly] = useState(null);
    const contactsLoadedRef = useRef(new Set()); // Rastrear contactos ya cargados

    const [contactModes, setContactModes] = useState({});

    const toggleContactMode = async (contactId, value) => {
        if (!contactId) return; // prevención extra
        setIsLoadingMode(true);
        await ApiClient.sendCurrentMode(contactId, value);
        setIsLoadingMode(false);
        setContactModes(prev => ({
            ...prev,
            [contactId]: value,
        }));
    };
    // obtener el modo actual según el contacto activo
    const currentMode = contactActive?.idContact
        ? Boolean(contactModes[contactActive.idContact])
        : false;

    // Inicializar verificación de autenticación al cargar
    useEffect(() => {
        if (AuthService.isAuthenticated()) {
            const storedUser = localStorage.getItem('userSystem');
            if (storedUser) {
                let userSystemStorage = JSON.parse(storedUser);
                setUserSystem(userSystemStorage);
                setUserLoggedIn(true);
                setIsLoadingContacts(true);
                setTypeMessagePermitted(userSystemStorage.permission ? userSystemStorage.permission : ['text'])
            }
        }

        // Suscribirse a eventos de autenticación
        const unsubscribe = AuthService.subscribe((event) => {
            switch (event) {
                case 'logout':
                    resetState();
                    break;
                case 'login':
                    const storedUser = localStorage.getItem('userSystem');
                    if (storedUser) {
                        let userSystemStorage = JSON.parse(storedUser);
                        setUserSystem(userSystemStorage);
                        setUserLoggedIn(true);
                        setIsLoadingContacts(true);
                        setTypeMessagePermitted(userSystemStorage.permission ? userSystemStorage.permission : ['text'])
                    }
                    break;
                default:
                    break;
            }
        });

        return () => unsubscribe();
    }, []);
    // Inicializar Ably cuando el usuario inicia sesión
    useEffect(() => {
        if (userLoggedIn) {
            // console.log('🔌 Inicializando conexión Ably para usuario autenticado');

            const conexion = iniciarConexionAbly(
                contactActive?.idContact,
                setMessagesUser,
                setContacts
            );

            setConexionAbly(conexion);

            return () => {
                if (conexion) {
                    try {
                        // console.log('🔌 Cerrando conexión Ably');
                        conexion.cerrar();
                    } catch (err) {
                        // console.error('Error al cerrar conexión Ably:', err);
                    }
                }
            };
        }
    }, [userLoggedIn]);

    // Manejar refresco de token
    useEffect(() => {
        if (userLoggedIn) {
            const intervalId = TokenService.startTokenRefreshInterval(logout);
            return () => TokenService.stopTokenRefreshInterval(intervalId);
        }
    }, [userLoggedIn]);

    // Cargar contactos cuando el usuario inicia sesión
    useEffect(() => {
        if (userLoggedIn) {
            loadContacts();
        }
    }, [userLoggedIn]);

    // Escuchar eventos de nuevos mensajes
    useEffect(() => {
        const handleNewMessage = (event) => {
            const { contactId, messageId } = event.detail;

            if (contactActive && contactActive.idContact.toString() === contactId) {
                // console.log(`📨 Evento de nuevo mensaje recibido para contacto activo: ${messageId}`);

                // Forzar actualización de UI
                setIsMessagesReady(false);
                setTimeout(() => {
                    setMessagesUser(current => [...current]);
                    setIsMessagesReady(true);
                }, 50);
            }
        };

        window.addEventListener('new-message-received', handleNewMessage);

        return () => {
            window.removeEventListener('new-message-received', handleNewMessage);
        };
    }, [contactActive]);

    // Actualizar canal Ably cuando cambia el contacto activo
    useEffect(() => {
        if (contactActive && contactActive.idContact && conexionAbly) {
            const contactId = contactActive.idContact.toString();
            // console.log(`👁️ Contacto activo cambiado a: ${contactId}`);

            // Establecer contacto activo en Ably
            conexionAbly.setActiveContact(contactId);

            // Suscribirse al canal del contacto
            conexionAbly.subscribeToContact(contactId);

            // Cargar mensajes
            loadContactMessages(contactId);

            // Marcar mensajes como leídos
            markMessagesAsRead(contactId);
        }
    }, [contactActive, conexionAbly]);

    // Suscribirse a canales de contactos recientes
    useEffect(() => {
        if (conexionAbly && contacts && contacts.length > 0 && userLoggedIn) {
            try {
                // console.log(`🔔 Configurando suscripciones Ably para ${contacts.length} contactos`);

                // Suscribirse a los contactos más recientes (limitado a 10)
                const recentContacts = contacts
                    .sort((a, b) => (b.last_message_at || 0) - (a.last_message_at || 0))
                    .slice(0, 10);

                recentContacts.forEach(contact => {
                    if (contact && contact.id) {
                        conexionAbly.subscribeToContact(contact.id);
                    }
                });
            } catch (error) {
                // console.error('Error al suscribirse a canales de contactos:', error);
            }
        }
    }, [conexionAbly, contacts, userLoggedIn]);

    // Manejar cambios de visibilidad para reconectar Ably si es necesario
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' &&
                conexionAbly?.ably?.connection.state === 'disconnected') {
                // console.log('🔄 Reconectando Ably al volver a la pestaña');
                conexionAbly.ably.connection.connect();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [conexionAbly]);

    // Cargar contactos desde la API
    const loadContacts = async () => {
        try {
            setIsLoadingContacts(true);
            const userData = JSON.parse(localStorage.getItem('userSystem'));

            if (!userData?.id) {
                throw new Error('ID de usuario no encontrado');
            }

            // Obtener lista de contactos
            const contactsData = await ApiClient.getContacts(userData.id);
            setContacts(contactsData);


        } catch (error) {
            // console.error('Error al cargar contactos:', error);
            AuthService.checkTokenExpiration(error);
        } finally {
            setIsLoadingContacts(false);
        }
    };

    // Cargar mensajes para un contacto específico
    const loadContactMessages = async (contactId) => {
        try {
            setIsLoadingMessage(true);
            // console.log(`🔄 Cargando mensajes para contacto ${contactId}`);

            // Cargar mensajes desde la API
            const response = await ApiClient.getMessages(contactId);

            if (response && response.data) {
                const messages = response.data.map(msg => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp)
                }));

                // Solo actualizar UI si sigue siendo el contacto activo
                if (contactActive && contactActive.idContact.toString() === contactId) {
                    setMessagesUser(messages);
                    setIsMessagesReady(true);
                }

                // Marcar como cargado
                contactsLoadedRef.current.add(contactId);
            }

            setIsLoadingMessage(false);
        } catch (error) {
            // console.error(`Error al cargar mensajes para contacto ${contactId}:`, error);
            AuthService.checkTokenExpiration(error);
            setIsLoadingMessage(false);
        }
    };

    // Marcar mensajes como leídos
    const markMessagesAsRead = async (contactId) => {
        try {
            await ApiClient.updateMessage(contactId);

            // Actualizar contador de mensajes no leídos en la lista de contactos
            setContacts(prevContacts =>
                prevContacts.map(contact =>
                    contact.id.toString() === contactId
                        ? { ...contact, received_messages_count: 0 }
                        : contact
                )
            );
        } catch (error) {
            // console.error('Error al marcar mensajes como leídos:', error);
            AuthService.checkTokenExpiration(error);
        }
    };

    // Enviar un mensaje
    const handleSendMessage = async (messageData) => {
        // ID temporal único para cualquier tipo de mensaje
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Objeto base para cualquier tipo de mensaje
        const baseMessage = {
            id: tempId,
            sender: 'user',
            timestamp: new Date(),
            status: 'sending'
        };

        let newMessage;
        let apiFunction;

        switch (messageData.type) {
            case 'text':
                // Mensaje de texto normal
                newMessage = {
                    ...baseMessage,
                    text: messageData.content,
                    type: 'text'
                };
                apiFunction = () => ApiClient.sendMessageToContact(
                    contactActive.idContact,
                    messageData.content
                );
                break;

            case 'image':
                newMessage = {
                    ...baseMessage,
                    type: 'image',
                    media_url: URL.createObjectURL(messageData.file.get('file')), // URL temporal para previsualización
                    caption: messageData.content,
                    fileName: messageData.fileInfo.name
                };
                apiFunction = () => ApiClient.sendFileToContact(
                    contactActive.idContact,
                    messageData.file,
                    'image',
                    messageData.content
                );
                break;

            case 'video':
                newMessage = {
                    ...baseMessage,
                    type: 'video',
                    media_url: URL.createObjectURL(messageData.file.get('file')),
                    caption: messageData.content,
                    fileName: messageData.fileInfo.name
                };
                apiFunction = () => ApiClient.sendFileToContact(
                    contactActive.idContact,
                    messageData.file,
                    'video',
                    messageData.content
                );
                break;

            case 'audio':
            case 'voice':
                newMessage = {
                    ...baseMessage,
                    type: messageData.type,
                    media_url: URL.createObjectURL(messageData.file.get('file')),
                    caption: messageData.content,
                    fileName: messageData.fileInfo.name
                };
                apiFunction = () => ApiClient.sendFileToContact(
                    contactActive.idContact,
                    messageData.file,
                    messageData.type,
                    messageData.content
                );
                break;

            case 'document':
                newMessage = {
                    ...baseMessage,
                    type: 'document',
                    media_url: URL.createObjectURL(messageData.file.get('file')),
                    caption: messageData.content,
                    fileName: messageData.fileInfo.name
                };
                apiFunction = () => ApiClient.sendFileToContact(
                    contactActive.idContact,
                    messageData.file,
                    'document',
                    messageData.content
                );
                break;
            default:
                break;
        }

        // Añadir a UI inmediatamente
        setMessagesUser(prev => [...prev, newMessage]);

        try {
            // Enviar a API usando la función correspondiente
            const response = await apiFunction();

            if (response && response.success) {
                // Actualizar con ID del servidor y estado
                setMessagesUser(prev =>
                    prev.map(msg =>
                        msg.id === tempId
                            ? {
                                ...msg,
                                id: response.data.id,
                                status: 'sent',
                                // Actualizar la URL temporal con la URL real del servidor si aplica
                                media_url: response.data.media_url || msg.media_url
                            }
                            : msg
                    )
                );
            } else {
                // Marcar como fallido
                setMessagesUser(prev =>
                    prev.map(msg =>
                        msg.id === tempId
                            ? { ...msg, status: 'failed' }
                            : msg
                    )
                );
            }
        } catch (error) {
            // Marcar como fallido
            setMessagesUser(prev =>
                prev.map(msg =>
                    msg.id === tempId
                        ? { ...msg, status: 'failed' }
                        : msg
                )
            );

            AuthService.checkTokenExpiration(error);
        }
    };

    // Reiniciar todos los estados
    const resetState = () => {
        setUserLoggedIn(false);
        setUserSystem(null);
        setOpenDrawer(false);
        setOpenConfig(false);
        setContacts([]);
        setMessagesUser([]);
        setIsLoadingMessage(false);
        setIsLoadingContacts(false);
        setIsMessagesReady(false);
        setContactActive(null);
        setTypeMessagePermitted(['text']);
        contactsLoadedRef.current = new Set();
    };

    // Manejar cierre de sesión
    const logout = async () => {
        try {
            await AuthService.logout();
        } catch (error) {
            resetState();
        }
    };

    // Seleccionar un contacto
    const selectUser = (idContact, nameContact, currentMode) => {
        if (!idContact || !nameContact) return;

        // console.log(`🔍 Seleccionando contacto: ${nameContact} (${idContact})`);

        // Limpiar mensajes y establecer carga
        setMessagesUser([]);
        setIsLoadingMessage(true);
        setIsMessagesReady(false);

        // Establecer contacto activo
        setContactActive({
            idContact,
            nameContact,
            currentMode,
            _timestamp: Date.now()
        });

        setContactModes(prev => ({
            ...prev,
            [idContact]: currentMode === "Ejecutivo" ? true : false
        }));

        // Cerrar drawer en móvil
        if (isMobile) {
            setOpenDrawer(false);
        }
    };
    return (
        <Box sx={{ display: 'flex', height: '100vh', width: '100%' }}>
            {isLoadingContacts ? (
                <LoadingProcess text="Cargando contactos..." tipo="lateral" />
            ) : (
                <Box>
                    <ContactMobil
                        openDrawer={openDrawer}
                        setOpenDrawer={setOpenDrawer}
                        openConfi={openConfi}
                        setOpenConfig={setOpenConfig}
                        isMobile={isMobile}
                        contacts={contacts}
                        selectUser={selectUser}
                        userSystem={userSystem}
                        logout={logout}
                        userLoggedIn={userLoggedIn}
                        contactActive={contactActive}
                    />
                    {!isMobile && (
                        <ContactDesktop
                            contacts={contacts}
                            selectUser={selectUser}
                            openConfi={openConfi}
                            setOpenConfig={setOpenConfig}
                            isMobile={isMobile}
                            userSystem={userSystem}
                            logout={logout}
                            userLoggedIn={userLoggedIn}
                            contactActive={contactActive}
                        />
                    )}
                </Box>
            )}

            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#222e35',
                backgroundImage: 'url(/images/fondo_6.avif)',
                backgroundAttachment: 'fixed',
                backgroundBlendMode: 'multiply',
                backgroundSize: 'contant',
                backgroundPosition: 'center',
                height: '100vh',
                marginLeft: isMobile || !userLoggedIn ? '0' : '432px',
            }}>
                <ChatHeader
                    isMobile={isMobile}
                    contactActive={contactActive}
                    setOpenDrawer={setOpenDrawer}
                    userSystem={userSystem}
                    userLoggedIn={userLoggedIn}
                    executiveMode={currentMode}
                    setExecutiveMode={(val) => toggleContactMode(contactActive?.idContact, val)}
                />

                <MessagesArea
                    key={`messages-${contactActive?.idContact || 'none'}`}
                    messagesUser={messagesUser}
                    isLoadingMessage={isLoadingMessage}
                    isLoadingCurrentMode={isLoadingCurrentMode}
                    userLoggedIn={userLoggedIn}
                    isMobile={isMobile}
                    setUserSystem={setUserSystem}
                    isMessagesReady={isMessagesReady}
                    setIsMessagesReady={setIsMessagesReady}
                    contactActive={contactActive}
                />
                <ChatInput
                    onSend={handleSendMessage}
                    userLoggedIn={userLoggedIn}
                    contactActive={contactActive}
                    typeMessagePermitted={typeMessagePermitted}
                    executiveMode={currentMode}
                />
            </Box>
        </Box>
    );
};

export default ChatWindow;